import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Button,
  CircularProgress,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { FlowDialog } from 'components/flowDialog/FlowDialog';
import { CreateDepartmentStep } from 'features/departments/createDepartmentFlow/CreateDepartmentStep';
import { AddAdminToDepartmentStep } from 'features/departments/createDepartmentFlow/AddAdminToDepartmentStep';
import { DepartmentsTable } from 'features/departments/DepartmentsTable';
import 'translations/translations';
import { useTranslation } from 'react-i18next';
import { ApplicationState } from 'store';
import { organizationActions } from 'actions/organizationActions';

const useStyles = makeStyles((theme) => ({
  alignRight: {
    textAlign: 'right',
  },
  buttonClass: {
    marginLeft: 0,
  },
  plusIcon: {
    display: 'inline-block',
    fontSize: '1.5rem',
    lineHeight: 0,
    marginRight: '0.5rem',
  },
  preDataView: {
    marginTop: '3rem',
    textAlign: 'center',
  },
  error: {
    color: theme.palette.error.main,
  },
}));

interface ParamTypes {
  orgId: string;
}

const DepartmentsTab: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation(['translations']);
  const [isOpen, setIsOpen] = useState(false);
  const { orgId } = useParams<ParamTypes>();

  const organization = useSelector(
    (state: ApplicationState) => state.organization?.organization,
  );

  const departmentName = useSelector(
    (state: ApplicationState) => state.department?.name,
  );

  const departmentId = useSelector(
    (state: ApplicationState) => state.department?.id,
  );

  const departmentError = useSelector(
    (state: ApplicationState) => state.organization?.error,
  );

  // Get organizationId from URL params or fallback to store
  const organizationId = orgId || organization?.organizationId;

  // Fetch organization data when component mounts (tab becomes active)
  useEffect(() => {
    if (organizationId) {
      dispatch(organizationActions.getOrganizationById(organizationId));
    }
  }, [organizationId, dispatch]);

  const handleOpenDialog = (): void => {
    dispatch({ type: 'SET_STEP', step: 1 });
    setIsOpen(true);
  };

  const handleCloseDialog = (): void => {
    setIsOpen(false);
    // Refetch organization data when dialog closes to get updated departments
    if (organizationId) {
      dispatch(organizationActions.getOrganizationById(organizationId));
    }
  };

  return (
    <>
      {!organization?.departments ? (
        <div className={classes.preDataView}>
          {departmentError ? (
            <Typography className={classes.error} variant="subtitle2">
              {departmentError}
            </Typography>
          ) : (
            <CircularProgress color="secondary" />
          )}
        </div>
      ) : (
        <div>
          <div className={classes.alignRight}>
            <Button
              variant="contained"
              color="primary"
              className={classes.buttonClass}
              onClick={handleOpenDialog}
            >
              <span className={classes.plusIcon}>+</span>
              {t('organization_page.create_dept_btn')}
            </Button>
          </div>
          <DepartmentsTable
            departments={organization?.departments}
            inTab={true}
          />
        </div>
      )}
      <FlowDialog
        fullWidth={false}
        isOpen={isOpen}
        handleClose={handleCloseDialog}
        title="organization_page.create_dept.dialog_title"
        message={t('organization_page.create_dept.success_snack')}
        snackLinkText={`${t('common.goto')} ${departmentName}`}
        snackLink={`${location.pathname}/department/${departmentId}`}
        contentHeight={'16.5rem'}
      >
        <CreateDepartmentStep
          stepTitleString="organization_page.create_dept.create_dept_step.title"
          buttonText="common.create_and_proceed"
        />
        <AddAdminToDepartmentStep
          stepTitleString="organization_page.create_dept.add_admin_step.title"
          buttonText="common.create"
        />
      </FlowDialog>
    </>
  );
};

export { DepartmentsTab };
